import React from "react";

const WarehouseSelectModal = ({ warehouses, item, cart, setCart, onClose }) => {
  if (!item) return null;

  // 1. Group stock entries by warehouse location ID
  const groups = {};
  warehouses.forEach(w => {
    const key = w.locationId || "unknown";
    if (!groups[key]) {
      groups[key] = {
        locationId: key,
        name: w.name,
        items: [],
        minExpiry: Infinity
      };
    }
    groups[key].items.push(w);
    
    const expTime = w.expiryDate ? new Date(w.expiryDate).getTime() : Infinity;
    if (expTime < groups[key].minExpiry) {
      groups[key].minExpiry = expTime;
    }
  });

  // 2. Sort the groups by their minExpiry (soonest first)
  const sortedGroups = Object.values(groups).sort((a, b) => a.minExpiry - b.minExpiry);

  // 3. For each group, sort its items chronologically by expiry date
  sortedGroups.forEach(g => {
    g.items.sort((a, b) => {
      const dateA = a.expiryDate ? new Date(a.expiryDate).getTime() : Infinity;
      const dateB = b.expiryDate ? new Date(b.expiryDate).getTime() : Infinity;
      return dateA - dateB;
    });
  });

  const handleAdd = (w) => {
    setCart(prev => {
      let currentCart = [...prev];
      const existingIndex = currentCart.findIndex(i => 
        i._id === item._id && 
        i.selectedWarehouseId === w.locationId && 
        (i.expiryDate === w.expiryDate || (!i.expiryDate && !w.expiryDate))
      );
      
      if (existingIndex >= 0) {
        const existing = currentCart[existingIndex];
        const newQty = Math.min(existing.qty + 1, w.quantity || 0);
        currentCart[existingIndex] = { ...existing, qty: newQty };
      } else {
        currentCart.push({ 
          ...item, 
          qty: 1, 
          manualAmount: null, 
          selectedWarehouseId: w.locationId, 
          selectedWarehouseName: w.name,
          expiryDate: w.expiryDate || null,
          batchId: w.batchId || null
        });
      }
      return currentCart;
    });
  };

  const handleRemove = (w) => {
    setCart(prev => {
      return prev.map(i => {
        const isMatch = i._id === item._id && 
                        i.selectedWarehouseId === w.locationId && 
                        (i.expiryDate === w.expiryDate || (!i.expiryDate && !w.expiryDate));
        if (isMatch) {
          return { ...i, qty: i.qty - 1 };
        }
        return i;
      }).filter(i => i.qty > 0);
    });
  };

  return (
    <>
      {/* BACKDROP - Clicking closes the modal */}
      <div
        className="fixed inset-0 bg-black/40 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* CENTER POPUP CONTAINER */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 pointer-events-none">
        {/* Outer container has flex layout with rounded-xl corners (both top and bottom) */}
        <div className="bg-white rounded-xl shadow-xl flex flex-col pointer-events-auto animate-in fade-in zoom-in duration-200 w-full max-w-lg overflow-hidden">
          
          {/* HEADER (cloned style, rounded upper corners, shrink-0, no borders) */}
          <div className="px-6 py-3 bg-[#172554] flex justify-between items-center text-white select-none rounded-t-xl shrink-0">
            <h2 className="text-sm font-semibold truncate max-w-[85%]">
              {item.name}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-white hover:text-gray-200 text-xl font-bold"
            >
              ✕
            </button>
          </div>

          {/* SCROLLABLE EDGE-TO-EDGE WAREHOUSE STACK LIST (pb-4 adds bottom padding for rounded corners) */}
          <div className="flex flex-col p-0 pb-4 overflow-y-auto max-h-[65vh]">
            {sortedGroups.map((group) => {
              const groupItems = group.items;
              return (
                <div key={group.locationId} className="flex flex-col">
                  {/* Warehouse Group Header Banner */}
                  <div className="bg-slate-100 px-4 py-2 font-bold text-xs text-[#172554] uppercase tracking-wider select-none border-b border-slate-200">
                    {group.name}
                  </div>
                  
                  {/* Rows stack for this warehouse group */}
                  <div className="divide-y divide-slate-100 bg-white">
                    {groupItems.map((w) => {
                      let expStr = "No Expiry";
                      if (w.expiryDate) {
                        try {
                          const d = new Date(w.expiryDate);
                          if (!isNaN(d.getTime())) {
                            expStr = `Exp: ${d.toLocaleDateString()}`;
                          }
                        } catch (err) {}
                      }
                      const batchStr = w.batchId && w.batchId !== "N/A" ? w.batchId : "";

                      // Find the current quantity in cart for this specific warehouse & expiry batch
                      const cartItem = cart.find(i => 
                        i._id === item._id && 
                        i.selectedWarehouseId === w.locationId && 
                        (i.expiryDate === w.expiryDate || (!i.expiryDate && !w.expiryDate))
                      );
                      const currentQty = cartItem ? cartItem.qty : 0;

                      return (
                        <div
                          key={w._id}
                          className="grid grid-cols-3 items-center px-4 py-3 hover:bg-slate-50/50 transition-colors select-none"
                        >
                          {/* Left: Expiry & Batch (Bold & Compact) */}
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <span className="text-sm font-extrabold text-slate-800 leading-tight">
                              {expStr}
                            </span>
                            {batchStr && (
                              <span className="text-[11px] font-mono font-bold text-slate-400">
                                Batch: {batchStr}
                              </span>
                            )}
                          </div>

                          {/* Middle: Qty available badge (Perfect Center) */}
                          <div className="flex justify-center">
                            <span className="text-xs font-extrabold text-blue-700 bg-blue-50 px-3 py-1 rounded-full border border-blue-100 uppercase tracking-tight shadow-sm">
                              {w.quantity} available
                            </span>
                          </div>

                          {/* Right: Quantity control buttons (Aligned Right) */}
                          <div className="justify-self-end">
                            <div className="flex items-center justify-between border border-blue-900 rounded bg-white shadow-sm overflow-hidden h-8 w-28">
                              <button
                                type="button"
                                className="px-3 h-full font-bold text-base text-blue-900 hover:bg-blue-50 transition disabled:opacity-30"
                                onClick={() => handleRemove(w)}
                                disabled={currentQty === 0}
                              >
                                −
                              </button>
                              <span className="font-extrabold text-sm text-slate-800">
                                {currentQty}
                              </span>
                              <button
                                type="button"
                                className="px-3 h-full font-bold text-base text-blue-900 hover:bg-blue-50 transition disabled:opacity-30"
                                onClick={() => handleAdd(w)}
                                disabled={currentQty >= (w.quantity ?? 0)}
                              >
                                +
                              </button>
                            </div>
                          </div>

                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </>
  );
};

export default WarehouseSelectModal;
